/**
 * Soft Spatial Observe — multi-camera selection Soft (Soft≠Live · 127).
 * AI would: Incident → Spatial Query → Coverage rank → OPEN_LIVE multi · never Live invent.
 */
import { haversineM } from "./soft-fleet-geo.js";

/** Spatial trigger default radius (metres) for Soft ENTER_RADIUS theatre. */
export const SPATIAL_TRIGGER_RADIUS_M = 30;

/**
 * Cameras / fixed Soft devices as observe endpoints (plant plane).
 * Soft only — does not read Live tenant cameras.
 */
export function softCameraEndpoints(assets) {
  return (assets || []).filter(
    (a) =>
      a &&
      a.kind === "device" &&
      Array.isArray(a.ll) &&
      a.ll.length >= 2 &&
      Number.isFinite(Number(a.ll[0])) &&
      Number.isFinite(Number(a.ll[1])),
  );
}

function bearingDeg(from, to) {
  if (!from || !to) return 0;
  const φ1 = (from[1] * Math.PI) / 180;
  const φ2 = (to[1] * Math.PI) / 180;
  const Δλ = ((to[0] - from[0]) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function aspectLabel(deg) {
  const d = ((Number(deg) % 360) + 360) % 360;
  if (d < 45 || d >= 315) return "front";
  if (d < 135) return "right";
  if (d < 225) return "rear";
  return "left";
}

/**
 * Rank Soft plant cameras around origin · multi-view pack.
 * @param {object[]} assets
 * @param {number[]} originLl [lng,lat]
 * @param {{ radiusM?: number, max?: number }} [opts]
 */
export function softSelectMultiView(assets, originLl, opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const radius =
    Number.isFinite(Number(o.radiusM)) && Number(o.radiusM) > 0
      ? Number(o.radiusM)
      : 180;
  const maxN =
    Number.isFinite(Number(o.max)) && Number(o.max) > 0
      ? Math.min(8, Math.floor(Number(o.max)))
      : 4;
  const origin = [
    Number(originLl && originLl[0]),
    Number(originLl && originLl[1]),
  ];
  if (!Number.isFinite(origin[0]) || !Number.isFinite(origin[1])) {
    return { origin: null, cameras: [], radiusM: radius };
  }

  const cams = softCameraEndpoints(assets)
    .map((c) => {
      const distM = haversineM(origin, c.ll);
      const brg = bearingDeg(origin, c.ll);
      const thermal = (c.skills || []).includes("thermal");
      const observe = (c.skills || []).includes("observe") || c.tier === "T2" || c.tier === "T3";
      let score = 0;
      if (distM <= radius) score += 40;
      score += Math.max(0, 30 - distM / 12);
      if (observe) score += 8;
      if (thermal) score += 5;
      if (c.surveyed) score += 3;
      return {
        id: String(c.id),
        label: String(c.label || c.id),
        ll: c.ll.slice(),
        distM: Math.round(distM),
        bearing: Math.round(brg),
        aspect: aspectLabel(brg),
        skills: c.skills || [],
        score,
        role: "candidate",
        liveState: "soft_poster", // never invent decoded frames
      };
    })
    .filter((c) => c.distM <= radius * 1.45)
    .sort((a, b) => b.score - a.score);

  /* Spread aspects if possible (front/left/right/rear). */
  const picked = [];
  const usedAspect = new Set();
  for (const c of cams) {
    if (picked.length >= maxN) break;
    if (!usedAspect.has(c.aspect) || picked.length >= maxN - 1) {
      picked.push({
        ...c,
        role:
          picked.length === 0
            ? "primary"
            : c.aspect === "rear"
              ? "rear"
              : c.aspect === "left" || c.aspect === "right"
                ? "side"
                : "support",
      });
      usedAspect.add(c.aspect);
    }
  }
  for (const c of cams) {
    if (picked.length >= maxN) break;
    if (picked.some((p) => p.id === c.id)) continue;
    picked.push({ ...c, role: "support" });
  }

  return {
    origin,
    radiusM: radius,
    cameras: picked.map((c, i) => ({
      ...c,
      slot: i,
      action: "OPEN_LIVE_SOFT",
      honesty:
        "Soft tile · browser Live player not wired · RTSP/μDock path may be Live elsewhere",
    })),
  };
}

/**
 * Soft ENTER_RADIUS spatial trigger · units within m of focus.
 */
export function softSpatialTriggerHits(assets, originLl, radiusM) {
  const r =
    Number.isFinite(Number(radiusM)) && Number(radiusM) > 0
      ? Number(radiusM)
      : SPATIAL_TRIGGER_RADIUS_M;
  const origin = [
    Number(originLl && originLl[0]),
    Number(originLl && originLl[1]),
  ];
  if (!Number.isFinite(origin[0]) || !Number.isFinite(origin[1])) {
    return { radiusM: r, hits: [] };
  }
  const hits = [];
  for (const a of assets || []) {
    if (!a || !Array.isArray(a.ll) || a.ll.length < 2) continue;
    if (a.kind === "device" && a.fixed) continue;
    const distM = haversineM(origin, a.ll);
    if (distM <= r) {
      hits.push({
        id: a.id,
        kind: a.kind,
        label: a.label || a.id,
        distM: Math.round(distM),
        free: !!a.free,
        plane: a.missionId ? "mission" : "mobile",
      });
    }
  }
  hits.sort((a, b) => a.distM - b.distM);
  return { radiusM: r, hits };
}

/**
 * Soft observe pack for Command Workbench from live Soft state.
 * @param {object} state
 * @param {{ origin?: number[], preferCamIds?: string[], radiusM?: number, zoneId?: string, zoneLabel?: string }} [opts]
 */
export function softObservePackOf(state, opts) {
  const s = state || {};
  const o = opts && typeof opts === "object" ? opts : {};
  const assets = s.assets || [];
  const leadInc = (s.incidents || []).find((i) => i && i.status !== "soft_contained");
  const mission = (s.missions || []).find((m) => m && m.status === "enroute") || (s.missions || [])[0];
  let origin = null;
  if (Array.isArray(o.origin) && o.origin.length >= 2) origin = o.origin;
  else if (leadInc && Array.isArray(leadInc.ll)) origin = leadInc.ll;
  else if (mission && mission.destDeviceId) {
    const dev = assets.find((a) => a && String(a.id) === String(mission.destDeviceId));
    if (dev && dev.ll) origin = dev.ll;
  } else if (Array.isArray(s.origin)) {
    origin = s.origin;
  }
  const radius =
    Number.isFinite(Number(o.radiusM)) && Number(o.radiusM) > 0
      ? Number(o.radiusM)
      : o.zoneId
        ? 140
        : 160;
  let multi = softSelectMultiView(assets, origin, { radiusM: radius, max: 4 });
  /* Zone-bound cameras jump the queue when their ll is known. Soft≠Live. */
  const prefer = Array.isArray(o.preferCamIds) ? o.preferCamIds : [];
  if (prefer.length) {
    const byId = new Map((multi.cameras || []).map((c) => [c.id, c]));
    const boosted = [];
    for (const id of prefer) {
      const sid = String(id || "").trim();
      if (!sid) continue;
      if (byId.has(sid)) {
        boosted.push({ ...byId.get(sid), role: "zone_bind", zonePreferred: true });
        byId.delete(sid);
        continue;
      }
      const a = assets.find((x) => x && String(x.id) === sid);
      if (a && a.ll) {
        boosted.push({
          id: sid,
          label: a.label || sid,
          ll: a.ll.slice(),
          distM: origin ? Math.round(haversineM(origin, a.ll)) : 0,
          bearing: 0,
          aspect: "zone",
          skills: a.skills || [],
          score: 999,
          role: "zone_bind",
          zonePreferred: true,
          liveState: "soft_poster",
          slot: boosted.length,
          action: "OPEN_LIVE_SOFT",
          honesty:
            "Zone bind Soft · browser Live player not wired · RTSP/μDock may be Live elsewhere",
        });
      }
    }
    for (const c of byId.values()) {
      if (boosted.length >= 6) break;
      boosted.push(c);
    }
    multi = {
      ...multi,
      cameras: boosted.map((c, i) => ({ ...c, slot: i })),
      zoneId: o.zoneId || null,
    };
  }
  const trigger = softSpatialTriggerHits(
    assets,
    origin,
    o.zoneId ? Math.max(SPATIAL_TRIGGER_RADIUS_M, 40) : SPATIAL_TRIGGER_RADIUS_M,
  );
  return {
    soft: true,
    incidentId: leadInc ? leadInc.id : null,
    missionId: mission ? mission.id : null,
    zoneId: o.zoneId || null,
    zoneLabel: o.zoneLabel || null,
    multiView: multi,
    spatialTrigger: {
      kind: o.zoneId ? "ZONE_ENTER_RADIUS" : "ENTER_RADIUS",
      ...trigger,
      actionsIfHit: ["OPEN_CAMERA_SOFT", "START_RECORDING_SOFT", "CREATE_EVIDENCE_SOFT"],
    },
    browserLive: {
      wired: false,
      path: "RTSP → μDock → Micro H264/H265 → browser player",
      status: "player_gap",
    },
  };
}

/** Soft REQUEST_SNAPSHOT action log row. */
export function softRequestSnapshotAction(cameraId, meta) {
  return {
    kind: "REQUEST_SNAPSHOT",
    cameraId: String(cameraId || ""),
    soft: true,
    say: `AI REQUEST_SNAPSHOT · ${cameraId || "—"} · Soft evidence seed`,
    ...(meta || {}),
  };
}
